import React from 'react';
import { UserGroupIcon, PlusIcon, EyeIcon } from '@heroicons/react/24/outline';
import ApiLoader from '../../ui/ApiLoader';
import { DataTable } from '../../common/DataTable';

const STATUS_OPTIONS = [
  { value: "New Lead", label: "New Lead" },
  { value: "Contacted", label: "Contacted" },
  { value: "Qualified", label: "Qualified" },
  { value: "Proposal Sent", label: "Proposal Sent" },
  { value: "Negotiation", label: "Negotiation" },
  { value: "Confirmed", label: "Confirmed" },
  { value: "Lost", label: "Lost" },
  { value: "On Hold", label: "On Hold" },
];

const CRMLeadsTab = ({
  loading,
  crmLeads,
  currentPage,
  totalCount,
  pageSize,
  isDarkMode,
  formatDate,
  setShowCreateCustomerModal,
  updateLeadStatus,
  setSelectedLead,
  setShowLeadDetailsModal,
  setCurrentPage,
  leadFilters,
  setLeadFilters
}) => {
  const columnFilters = [
    leadFilters?.company_name ? { id: "company_name", value: leadFilters.company_name } : null,
    leadFilters?.contact ? { id: "contact", value: leadFilters.contact } : null,
    leadFilters?.status ? { id: "status", value: leadFilters.status } : null,
    leadFilters?.industry ? { id: "industry", value: leadFilters.industry } : null,
    leadFilters?.from_date || leadFilters?.to_date
      ? { id: "next_follow_up", value: [leadFilters?.from_date || null, leadFilters?.to_date || null] }
      : null,
  ].filter(Boolean);

  const handleColumnFiltersChange = (updater) => {
    const current = columnFilters;
    const next = typeof updater === "function" ? updater(current) : updater;
    const byId = Object.fromEntries(next.map((f) => [f.id, f.value]));
    const [fromDate, toDate] = byId.next_follow_up || [null, null];
    setLeadFilters({
      company_name: byId.company_name || "",
      contact: byId.contact || "",
      status: byId.status || "",
      industry: byId.industry || "",
      from_date: fromDate || "",
      to_date: toDate || "",
    });
    setCurrentPage(1);
  };

  const statusColor = (status) => ({
    backgroundColor:
      status === 'Confirmed' ? (isDarkMode ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5') :
      status === 'Qualified' ? (isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe') :
      status === 'Contacted' ? (isDarkMode ? 'rgba(251, 191, 36, 0.2)' : '#fef3c7') :
      status === 'New Lead' ? (isDarkMode ? 'rgba(156, 163, 175, 0.2)' : '#f3f4f6') :
      status === 'Lost' ? (isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2') :
      status === 'Proposal Sent' ? (isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff') :
      status === 'Negotiation' ? (isDarkMode ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa') :
      (isDarkMode ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff'),
    color:
      status === 'Confirmed' ? (isDarkMode ? '#86efac' : '#065f46') :
      status === 'Qualified' ? (isDarkMode ? '#93c5fd' : '#1e40af') :
      status === 'Contacted' ? (isDarkMode ? '#fcd34d' : '#92400e') :
      status === 'New Lead' ? (isDarkMode ? '#d1d5db' : '#1f2937') :
      status === 'Lost' ? (isDarkMode ? '#fca5a5' : '#991b1b') :
      status === 'Proposal Sent' ? (isDarkMode ? '#a5b4fc' : '#3730a3') :
      status === 'Negotiation' ? (isDarkMode ? '#fdba74' : '#9a3412') :
      (isDarkMode ? '#d8b4fe' : '#6b21a8'),
    cursor: status === 'Confirmed' ? 'not-allowed' : 'pointer',
    opacity: status === 'Confirmed' ? 0.75 : 1,
  });

  const columns = [
    {
      id: "company_name",
      accessorFn: (lead) => lead.company_name,
      header: "Company",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Company", placeholder: "Search company" } },
      cell: ({ row }) => (
        <div>
          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">{row.original.company_name}</div>
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{row.original.industry}</div>
        </div>
      ),
    },
    {
      id: "contact",
      accessorFn: (lead) => lead.primary_contact_name,
      header: "Contact",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Contact", placeholder: "Search contact or email" } },
      cell: ({ row }) => (
        <div>
          <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">{row.original.primary_contact_name}</div>
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{row.original.primary_contact_info?.email}</div>
        </div>
      ),
    },
    {
      id: "status",
      accessorFn: (lead) => lead.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: STATUS_OPTIONS,
        },
      },
      cell: ({ row }) => {
        const lead = row.original;
        return (
          <select
            value={lead.status}
            onChange={(e) => updateLeadStatus(lead.customer_id, e.target.value)}
            disabled={lead.status === 'Confirmed'}
            style={statusColor(lead.status)}
            className="px-2 py-1 text-xs leading-5 font-semibold rounded-full border-0 focus:ring-2 focus:ring-blue-500"
          >
            <option value="New Lead">New Lead</option>
            <option value="Contacted">Contacted</option>
            <option value="Qualified">Qualified</option>
            <option value="Proposal Sent">Proposal Sent</option>
            <option value="Negotiation">Negotiation</option>
            {lead.status === 'Confirmed' && <option value="Confirmed">Confirmed</option>}
            <option value="Lost">Lost</option>
          </select>
        );
      },
    },
    {
      id: "industry",
      accessorFn: (lead) => lead.industry,
      header: "Lead Source",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Industry", placeholder: "Search industry" } },
      cell: ({ row }) => (
        <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{row.original.lead_source || 'N/A'}</span>
      ),
    },
    {
      id: "next_follow_up",
      accessorFn: (lead) => lead.next_follow_up_date,
      header: "Next Follow-up",
      enableSorting: false,
      meta: { filter: { variant: "date-range", label: "Follow-up Date" } },
      cell: ({ row }) => (
        <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          {row.original.next_follow_up_date ? formatDate(row.original.next_follow_up_date) : 'Not scheduled'}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          onClick={() => {
            setSelectedLead(row.original);
            setShowLeadDetailsModal(true);
          }}
          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <EyeIcon className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header with Actions */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
          <div>
            <h2 className="text-lg sm:text-xl font-bold flex items-center">
              <UserGroupIcon className="w-5 h-5 text-purple-500 mr-2" />
              CRM Leads & Sales Pipeline
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
              Manage leads, track follow-ups, and convert to active customers
            </p>
          </div>
          <button
            onClick={() => setShowCreateCustomerModal(true)}
            className="flex items-center space-x-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-xs sm:text-sm"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add New Lead</span>
          </button>
        </div>

        {loading && crmLeads.length === 0 ? (
          <ApiLoader label="Loading leads..." className="py-20" />
        ) : (
          <DataTable
            columns={columns}
            data={crmLeads}
            getRowId={(lead) => String(lead.customer_id)}
            loading={loading}
            manualPagination
            rowCount={totalCount}
            pagination={{ pageIndex: (currentPage || 1) - 1, pageSize }}
            onPaginationChange={(updater) => {
              const current = { pageIndex: (currentPage || 1) - 1, pageSize };
              const next = typeof updater === "function" ? updater(current) : updater;
              setCurrentPage(next.pageIndex + 1);
            }}
            enableColumnFilters
            manualFiltering
            columnFilters={columnFilters}
            onColumnFiltersChange={handleColumnFiltersChange}
            emptyMessage='No CRM leads found. Click "Add New Lead" to get started.'
            enableColumnVisibility={false}
            enableColumnResizing
            maxBodyHeight="65vh"
          />
        )}
      </div>
    </div>
  );

};

export default CRMLeadsTab;
